import { Box, Chip, Collapse, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import type { DateTime } from "luxon";
import { type FC, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { useHospitalData } from "../../contexts/HospitalDataProvider";
import { useExpandedGroups } from "../../hooks/useExpandedGroups";
import { formatHoursMinutes } from "../../utils/dateUtils";
import type { Nurse, SpecificPreference } from "../../utils/models";
import { ExternalTimeOffIndicator } from "../ExternalTimeOffIndicator/ExternalTimeOffIndicator";
import { CensusGroupHeaderContent } from "../StaffTable/CensusGroupHeaderContent";
import { isRelevantPTO } from "../StaffTable/utils";
import ColorfulRole from "../UIToolkit/ColorfulRole/ColorfulRole";

const PTO_GROUP_KEY = "pto";

type PtoGapViewSectionProps = {
	date: DateTime;
	selectedNurse?: Nurse | null;
};

export const PtoGapViewSection: FC<PtoGapViewSectionProps> = ({ date, selectedNurse }) => {
	const { t } = useTranslation();
	const { hospitalData, getNurseFormattedName, chosenDepartment } = useHospitalData();
	const navigate = useNavigate();
	const location = useLocation();

	const onDoubleClickNurse = useCallback(
		(nurseId: number) => {
			navigate(`/${chosenDepartment.department_id}/nurse/${nurseId}`, {
				state: { navigationSrc: location.pathname },
			});
		},
		[navigate, chosenDepartment.department_id, location.pathname],
	);

	const relevantPreference = useCallback(
		(nurse: Nurse): SpecificPreference | undefined => nurse.employeeSpecificPreferences.find((pref) => isRelevantPTO(pref, date, chosenDepartment.dayStart)),
		[date, chosenDepartment.dayStart],
	);

	const relevantPTONurses = useMemo<Nurse[]>(() => {
		return hospitalData.nurses.filter((nurse) => {
			const hasPTO = nurse.employeeSpecificPreferences.some((pref) => isRelevantPTO(pref, date, chosenDepartment.dayStart));
			if (selectedNurse) {
				return hasPTO && nurse.id === selectedNurse.id;
			}
			return hasPTO;
		});
	}, [hospitalData.nurses, date, selectedNurse, chosenDepartment.dayStart]);

	// A pick narrows this list to that person, so a non-empty list means the time-off section is where
	// they are — open it. Empty while nobody is picked, so the section stays as the user left it.
	const autoExpandedGroups = useMemo<ReadonlySet<string>>(
		() => (selectedNurse && relevantPTONurses.length > 0 ? new Set([PTO_GROUP_KEY]) : new Set()),
		[selectedNurse, relevantPTONurses.length],
	);
	const { expandedGroups, toggleGroupExpand } = useExpandedGroups({ autoExpandedGroups });
	const isCollapsed = !expandedGroups.has(PTO_GROUP_KEY);

	if (relevantPTONurses.length === 0) return null;

	return (
		<Box
			sx={{
				width: "100%",
				display: "flex",
				flexDirection: "column",
			}}
		>
			<CensusGroupHeaderContent
				toggleGroupExpand={() => toggleGroupExpand(PTO_GROUP_KEY)}
				name={t("pto")}
				isCollapsed={isCollapsed}
				stats={[{ value: relevantPTONurses.length, label: "staff" }]}
			/>
			<Collapse in={!isCollapsed} timeout="auto" unmountOnExit>
				<Box
					sx={{
						display: "flex",
						flexDirection: "column",
					}}
				>
					{relevantPTONurses.map((nurse) => {
						const preference = relevantPreference(nurse);
						const externalTimeOff = nurse.employeeSpecificPreferences.find(
							(pref) => isRelevantPTO(pref, date, chosenDepartment.dayStart) && pref.external_time_off,
						)?.external_time_off;
						return (
							<Box
								key={`pto-gap-${nurse.id}`}
								onDoubleClick={() => onDoubleClickNurse(nurse.id)}
								sx={{
									display: "flex",
									alignItems: "center",
									gap: 1,
									px: 1.25,
									py: 0.25,
									bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
									borderBottom: "2px solid #E7E9EF",
									cursor: "pointer",
									height: "37px",
									fontSize: "0.875rem",
									color: "primary.dark",
									"&:hover": { bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08) },
								}}
							>
								<Box sx={{ display: "flex", alignItems: "center", gap: 1, maxWidth: "100px", flexWrap: "wrap" }}>
									<ColorfulRole role={nurse.role} />
								</Box>
								<Typography sx={{ fontSize: "14px", whiteSpace: "nowrap" }}>{getNurseFormattedName(nurse.id)}</Typography>
								<Typography sx={{ fontSize: "14px", color: "primary.dark", opacity: 0.5 }}>{formatHoursMinutes(preference?.hours ?? 0)}</Typography>
								<Box sx={{ ml: "auto", display: "flex", alignItems: "center", gap: 0.5 }}>
									<ExternalTimeOffIndicator externalTimeOff={externalTimeOff} />
									<Chip label={t("pto")} size="small" sx={{ backgroundColor: "primary.main", color: "white", fontWeight: "600" }} />
								</Box>
							</Box>
						);
					})}
				</Box>
			</Collapse>
		</Box>
	);
};
